import { useContext, useEffect, useRef } from 'react';
import CoreScroll from '@nrk/core-scroll';

import { ReactComponent as Chevron } from '@rikstv/play-common/src/icons/svg/chevron-light.svg';
import { linearCoverpageFunnel } from '@rikstv/play-common/src/utils/analytics/posthogProvider';
import { H2, Loader } from '@rikstv/shared-components';

import { Scroller } from '../../scroller/Scroller';
import { useTvGuideChannels } from '../../tvGuide-v2/useTvGuideChannels';
import { ChannelContext } from '../CoverPageChannel';

import { ChannelOverview } from './ChannelOverview';

import styles from './ChannelList.module.css';

export const ChannelList = () => {
  const { channels } = useTvGuideChannels({ includePrograms: false });
  const currentChannelId = useContext(ChannelContext);
  const scrollContainerRef = useRef<CoreScroll['el'] | null>(null);
  const subscribedChannels = channels?.filter(channel => channel.subscription === true) || [];

  // Scroll to current channel when it changes (might change from ChannelGrid)
  useEffect(() => {
    if (!scrollContainerRef.current || !channels) return;

    const newChannelIndex = channels.findIndex(channel => (channel?.channelId ?? '').toString() === currentChannelId);

    if (newChannelIndex >= 0) {
      scrollContainerRef.current.scroll?.(scrollContainerRef.current.items[newChannelIndex]);
    }
  }, [currentChannelId, channels]);

  if (!channels) {
    return (
      <div className={styles.container}>
        <Loader size="large" />
      </div>
    );
  }

  const initialElementIndex = channels.findIndex(channel => {
    return (channel?.channelId ?? '').toString() === currentChannelId;
  });

  function scrollToStart() {
    scrollContainerRef.current?.scroll(scrollContainerRef.current.items[0]);
  }

  if (subscribedChannels.length === 0) {
    return null;
  }

  return (
    <div className={styles.container} style={{ '--scroller-margin': 'var(--rds-spacing--48)' } as React.CSSProperties}>
      <H2 className="sr-only">Kanalvelger</H2>
      <Scroller
        ref={scrollContainerRef}
        initialElementIndex={initialElementIndex}
        scrollToStartButton={
          <button
            aria-label="Gå til starten av kanallisten"
            className={styles.scrollToStartButton}
            onClick={scrollToStart}>
            <Chevron height="24px" width="24px" />
            Til start
          </button>
        }>
        {subscribedChannels.map((channel, idx) => (
          <ChannelOverview
            onClick={() =>
              linearCoverpageFunnel.captureClickFromChannelList(channel.channelId, idx, initialElementIndex)
            }
            key={`channel-overview-${channel.channelId}`}
            className={styles.fixOverviewSize}
            channel={channel}
            currentChannel={channel.channelId.toString() === currentChannelId}
          />
        ))}
      </Scroller>
    </div>
  );
};
